import merge from 'lodash/merge'
import { DeepPartial, ISeriesApi, Time } from "lightweight-charts"
import BaseChartSeries from "./baseSeries"
import { ChartCandle, RenkoSeriesOptions } from "../chart.typings"
import { BLANK_SERIES_ID, renkoSeriesOptions } from "../../../assets/constants"
import { deepClone } from '@mui/x-data-grid/utils/utils'
import { atr } from '../../../util/IndicatorUtils/core/indicators'

class RenkoSeries extends BaseChartSeries<ChartCandle, RenkoSeriesOptions> {
    projectedSeriesApi: ISeriesApi<any> | undefined = undefined;
    projectiondata: ChartCandle[] = []
    brickSize: number = 0


    addSeriesToChart() {
        return this.chart.chartApi.addCandlestickSeries(this.seriesOptions)
    }

    getDefaultOptions(): RenkoSeriesOptions {
        return merge(renkoSeriesOptions, this.options)
    }

    getPrimaryData(length = 0): ChartCandle[] {
        return length > 0 ? this.data.slice(-length) : this.data;
    }

    _update(candle: ChartCandle, metadata?: Record<string, any>) {
        this.metadata = metadata || {};
        const lastCandle = this.rawData[this.rawData.length - 1];
        if (lastCandle && lastCandle.time === candle.time) {
            this.rawData[this.rawData.length - 1] = candle
        } else {
            this.rawData.push(candle);
        }
        this.updateData(metadata)

        let lastRenkoCandle = this.data[this.data.length - 1]
        if (lastRenkoCandle) {
            this.generateAndSetProjectionCandles(lastRenkoCandle, candle)
        }
    }

    onOptionsUpdate(options: DeepPartial<RenkoSeriesOptions>): void {
        const { boxMethod, boxSize, atr, percentage, projectionDownColor, projectionUpColor, projectionDownBorderColor, projectionUpBorderColor, wickVisible } = options
        if (boxMethod || boxSize || atr || percentage || wickVisible !== undefined) {
            this.brickSize = 0
            this._setData(this.rawData, this.metadata)
            this.chart.refreshNonPrimaryGroups();
            this.chart.checkIfDataRequired();
        }
        if (projectionDownColor || projectionUpColor || projectionDownBorderColor || projectionUpBorderColor) {
            this.projectedSeriesApi?.applyOptions({
                upColor: projectionUpColor,
                downColor: projectionDownColor,
                borderUpColor: projectionUpBorderColor,
                borderDownColor: projectionDownBorderColor,
            })
        }
    }

    _setData(data: ChartCandle[], metadata?: Record<string, any>) {
        this.metadata = metadata || {};
        this.setData(data, metadata)
        this.seriesApi.setData(this.data)
        if (this.projectedSeriesApi) {
            this.projectedSeriesApi.setData(this.projectiondata)
        } else {
            this.projectedSeriesApi = this.chart.chartApi.addCandlestickSeries({
                ...this.seriesOptions,
                upColor: this.seriesOptions.projectionUpColor,
                downColor: this.seriesOptions.projectionDownColor,
                borderUpColor: this.seriesOptions.projectionUpBorderColor,
                borderDownColor: this.seriesOptions.projectionDownBorderColor,
                priceLineVisible: false
            })
            this.projectedSeriesApi.setData(this.projectiondata)
        }
    }

    private validateRenkoPreconditions(): string | null {
        const tentativeBrickSize = this.computeBrickSize(this.rawData) ?? 0
        if (!tentativeBrickSize || tentativeBrickSize === 0) {
            const { boxMethod, atr } = this.options as RenkoSeriesOptions
            if (boxMethod === 'atr') {
                return `ATR(${atr}) requires at least ${atr} candles. Only ${this.rawData.length} available.`
            }
            return 'Brick size could not be computed. Please check Renko settings.'
        }
        return null
    }

    generateSeriesData(data: ChartCandle[], metadata: Record<string, any> = {}, update = false): ChartCandle[] {
        if (this.config.id === BLANK_SERIES_ID) {
            return data
        }
        const rawData = this.rawData;
        if (rawData.length === 0) return []
        const candlesticks: ChartCandle[] = deepClone(data)
        if ((candlesticks?.length ?? 0) === 0) return [];

        const validationError = this.validateRenkoPreconditions()
        if (validationError) {
            this.metadata = {
                ...metadata,
                chartError: validationError
            }
            return []
        }

        const popedCandle = candlesticks.pop()
        const renkoCandles: ChartCandle[] = this.generateRenkoCandles(candlesticks)


        let incompleteCandle: ChartCandle | undefined = undefined
        this.projectiondata = incompleteCandle ? [incompleteCandle] : []
        if (!update && popedCandle) {
            const [lastRenkoCandle] = renkoCandles.slice(-1)
            if (lastRenkoCandle) {
                this.generateAndSetProjectionCandles(lastRenkoCandle, popedCandle, true)
            }
        }
        return update ? this.data : renkoCandles
    }

    generateRenkoCandles(candlesticks: ChartCandle[], lastRenkoCandle: ChartCandle | null = null) {
        if (!this.brickSize) {
            this.brickSize = this.computeBrickSize(this.rawData) ?? 1
        }
        const brickSize = this.brickSize
        const wickVisible = this.seriesOptions.wickVisible ?? true
        let lastRenko: ChartCandle | null = lastRenkoCandle
        const renkoCandles: ChartCandle[] = []
        let accumulatedHigh = 0
        let accumulatedLow = Infinity
        let accumulatedVolume = 0

        for (let i = 0; i < candlesticks.length; i++) {
            if (lastRenko === null) {
                lastRenko = candlesticks[i];
                renkoCandles.push(lastRenko);
                continue;
            }
            const currentCandle = candlesticks[i];
            let lastRenkoMin = Math.min(lastRenko.close, lastRenko.open);
            let lastRenkoMax = Math.max(lastRenko.close, lastRenko.open);

            accumulatedHigh = Math.max(currentCandle.high, accumulatedHigh)
            accumulatedLow = Math.min(currentCandle.low, accumulatedLow)
            accumulatedVolume += currentCandle.volume

            let time: number = NaN
            if (brickSize > 0 && ((currentCandle.close >= lastRenkoMax + brickSize) || (currentCandle.close <= lastRenkoMin - brickSize))) {
                const newRenkoCandles: ChartCandle[] = []
                while ((currentCandle.close >= lastRenkoMax + brickSize) || (currentCandle.close <= lastRenkoMin - brickSize)) {
                    let reference: number = currentCandle.close <= lastRenkoMin - brickSize ? lastRenkoMin : lastRenkoMax
                    const isDownBrick = reference > currentCandle.close
                    if (Number.isNaN(time)) {
                        time = (currentCandle.time) as number
                    } else {
                        time += 0.001
                    }
                    const newRenko: ChartCandle = {
                        ...currentCandle,
                        open: reference,
                        close: isDownBrick ? (reference - brickSize) : (reference + brickSize),
                        high: isDownBrick ? reference : (reference + brickSize),
                        low: isDownBrick ? (reference - brickSize) : reference,
                        time: time as Time,
                        volume: 0
                    }

                    newRenkoCandles.push(newRenko)
                    lastRenko = newRenko
                    lastRenkoMin = Math.min(lastRenko.close, lastRenko.open)
                    lastRenkoMax = Math.max(lastRenko.close, lastRenko.open)
                }

                const volumePerBrick = accumulatedVolume / newRenkoCandles.length
                newRenkoCandles.forEach((c) => {
                    c.volume = volumePerBrick
                    renkoCandles.push(c)
                })

                if (wickVisible && newRenkoCandles.length > 0) {
                    const lastCreatedBrick = renkoCandles[renkoCandles.length - 1]
                    const isDownBrick = lastCreatedBrick.close < lastCreatedBrick.open
                    if (isDownBrick) {
                        const brickOpen = lastCreatedBrick.open
                        if (accumulatedHigh > brickOpen && accumulatedHigh < brickOpen + brickSize) {
                            lastCreatedBrick.high = accumulatedHigh
                        }
                    }
                    if (!isDownBrick) {
                        const brickOpen = lastCreatedBrick.open
                        if (accumulatedLow < brickOpen && accumulatedLow > brickOpen - brickSize) {
                            lastCreatedBrick.low = accumulatedLow
                        }
                    }
                    renkoCandles[renkoCandles.length - 1] = lastCreatedBrick
                }
                accumulatedHigh = 0
                accumulatedLow = Infinity
                accumulatedVolume = 0
            } else {
                const lastIndex = renkoCandles.length - 1
                if (wickVisible && lastIndex >= 0 && renkoCandles[lastIndex]) {
                    const currentBrick = renkoCandles[lastIndex]
                    const isDownBrick = currentBrick.close < currentBrick.open

                    if (isDownBrick) {
                        const brickOpen = currentBrick.open
                        if (accumulatedHigh > brickOpen && accumulatedHigh < brickOpen + brickSize) {
                            currentBrick.high = Math.max(currentBrick.high, accumulatedHigh)
                        }
                    } else {
                        const brickOpen = currentBrick.open
                        if (accumulatedLow < brickOpen && accumulatedLow > brickOpen - brickSize) {
                            currentBrick.low = Math.min(currentBrick.low, accumulatedLow)
                        }
                    }

                    renkoCandles[lastIndex] = currentBrick
                }
            }
        }

        return renkoCandles
    }

    generateAndSetProjectionCandles(lastRenkoCandle: ChartCandle, candle: ChartCandle, generateOnly = false) {
        if (!this.brickSize || this.brickSize <= 0) return

        let lastRenkoMax = Math.max(lastRenkoCandle.close, lastRenkoCandle.open);
        let lastRenkoMin = Math.min(lastRenkoCandle.close, lastRenkoCandle.open);
        let time: number = NaN

        const newRenkoCandles: ChartCandle[] = []
        if ((candle.close >= lastRenkoMax + this.brickSize) || (candle.close <= lastRenkoMin - this.brickSize)) {
            while ((candle.close >= lastRenkoMax + this.brickSize) || (candle.close <= lastRenkoMin - this.brickSize)) {
                let reference: number = candle.close <= lastRenkoMin - this.brickSize ? lastRenkoMin : lastRenkoMax
                if (Number.isNaN(time)) {
                    time = (candle.time) as number
                } else {
                    time += 0.001
                }
                const newRenko: ChartCandle = {
                    ...candle,
                    open: reference,
                    close: reference > candle.close ? (reference - this.brickSize) : (reference + this.brickSize),
                    high: reference > candle.close ? reference : (reference + this.brickSize),
                    low: reference > candle.close ? (reference - this.brickSize) : reference,
                    time: time as Time
                }
                if (newRenko) newRenkoCandles.push(newRenko)
                lastRenkoCandle = newRenko
                lastRenkoMin = Math.min(lastRenkoCandle.close, lastRenkoCandle.open);
                lastRenkoMax = Math.max(lastRenkoCandle.close, lastRenkoCandle.open);
            }
        }

        const prevHigh = Math.max(lastRenkoCandle.close, lastRenkoCandle.open);
        const prevLow = Math.min(lastRenkoCandle.close, lastRenkoCandle.open);
        const newProjectionCandle = {
            ...candle
        }
        if (Number(lastRenkoCandle.time).toFixed(0) === Number(newProjectionCandle.time).toFixed(0)) {
            newProjectionCandle.time = (Number(lastRenkoCandle.time) + 0.01) as Time
        }
        if (newProjectionCandle.close <= prevHigh && newProjectionCandle.close >= prevLow) {
            newProjectionCandle.open = newProjectionCandle.close
            newProjectionCandle.high = newProjectionCandle.close
            newProjectionCandle.low = newProjectionCandle.close;
        } else if (newProjectionCandle.close > prevHigh) {
            newProjectionCandle.open = prevHigh
            newProjectionCandle.high = newProjectionCandle.close
            newProjectionCandle.low = prevHigh
        } else if (newProjectionCandle.close < prevLow) {
            newProjectionCandle.open = prevLow
            newProjectionCandle.high = prevLow
            newProjectionCandle.low = newProjectionCandle.close
        }
        if (
            newProjectionCandle.open !== lastRenkoCandle.open ||
            newProjectionCandle.close !== lastRenkoCandle.close
        ) {
            newRenkoCandles.push(newProjectionCandle)
        }


        this.projectiondata = newRenkoCandles
        if (this.projectedSeriesApi && !generateOnly) this.projectedSeriesApi.setData(this.projectiondata.length ? this.projectiondata : [{
            ...lastRenkoCandle,
            color: 'transparent',
            visible: false,
            time: (Number(lastRenkoCandle.time) + 0.01) as Time
        }])
    }

    computeBrickSize(data: ChartCandle[]): number | undefined {
        if (data.length === 0) return 0;

        const [lastCandle] = data.slice(-1);
        const ltp = lastCandle.close
        const { boxMethod, boxSize, atr: atrLength, percentage } = this.options as RenkoSeriesOptions;
        const security = this.chart.securities?.[0]
        const tickSize = security?.tickSize || 0.5
        const priceMultiplier = security?.priceMultiplier ?? 100
        const tickSizeInPrice = tickSize / priceMultiplier
        let calculatedBrickSize: number;
        switch (boxMethod) {
            case 'traditional':
                calculatedBrickSize = boxSize || 1;
                break;
            case 'percentage':
                if (percentage === undefined) {
                    throw new Error('percentageLTP must be defined when boxMethod is "percentage"');
                }
                calculatedBrickSize = Number((ltp * percentage / 100).toFixed(4));
                calculatedBrickSize = Math.round(calculatedBrickSize / tickSizeInPrice) * tickSizeInPrice

                if (calculatedBrickSize < tickSizeInPrice) {
                    calculatedBrickSize = tickSizeInPrice
                }
                break;
            case 'atr':
                if (data.length < (atrLength ?? 0)) return 0;
                const high: number[] = [], low: number[] = [], close: number[] = [];
                const slice = data.slice(-(atrLength ?? 0));
                slice.forEach(candle => {
                    high.push(candle.high);
                    low.push(candle.low);
                    close.push(candle.close)
                })

                const atrData = atr(high, low, close, atrLength ?? 1)
                const atrValue = atrData.length ? atrData[atrData.length - 1] : 0

                calculatedBrickSize = Math.round(atrValue / tickSizeInPrice) * tickSizeInPrice

                if (calculatedBrickSize < tickSizeInPrice) {
                    calculatedBrickSize = tickSizeInPrice
                }

                return Number(calculatedBrickSize.toFixed(4));
        }
        return calculatedBrickSize;
    }

    destroy(): void {
        if (this.projectedSeriesApi) {
            this.chart.chartApi.removeSeries(this.projectedSeriesApi)
        }
        super.destroy()
    }
}

export default RenkoSeries